import * as THREE from "three";
import type { ProjectionParams } from "../config/types";

// 캐시된 임시 객체 (매 프레임 새 객체 생성 방지)
const _xVec = new THREE.Vector3();
const _yVec = new THREE.Vector3();
const _zVec = new THREE.Vector3();
const _mat = new THREE.Matrix4();
const _defaultOffset = new THREE.Vector3(0, 0, 0);

// 캐싱 시스템: calculateObliqueMatrix 결과를 저장하여 중복 계산 방지
let _cachedMatrix: THREE.Matrix4 | null = null;
let _cachedParams: ProjectionParams | null = null;
let _cachedOffset = new THREE.Vector3();
let _isDirty = true;

/**
 * 캐시 무효화 - 파라미터가 변경될 때 호출
 */
export function invalidateObliqueCache() {
  _isDirty = true;
}

/**
 * Oblique 투영 행렬 계산 (캐싱 적용)
 * 아이소메트릭/디메트릭 뷰를 위한 커스텀 투영 행렬 생성
 * 
 * 최적화: 동일한 params와 panOffset일 때는 캐시된 결과 반환
 * 
 * 주의: 반환된 행렬은 캐시된 객체이므로 호출자가 수정하거나 저장하면 안됨
 * 저장이 필요한 경우 .clone()을 호출할 것
 */
export function calculateObliqueMatrix(
  params: ProjectionParams,
  panOffset = _defaultOffset,
): THREE.Matrix4 {
  // 캐시 히트 체크: params와 panOffset이 동일하면 캐시 반환
  if (
    !_isDirty &&
    _cachedMatrix &&
    _cachedParams &&
    _cachedParams.scaleX === params.scaleX &&
    _cachedParams.scaleY === params.scaleY &&
    _cachedParams.scaleZ === params.scaleZ &&
    _cachedParams.thetaX === params.thetaX &&
    _cachedParams.thetaY === params.thetaY &&
    _cachedParams.thetaZ === params.thetaZ &&
    _cachedOffset.equals(panOffset)
  ) {
    return _cachedMatrix;
  }
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const sx = params.scaleX;
  const sy = params.scaleY;
  const sz = params.scaleZ;

  const rx = toRad(params.thetaX);
  const ry = toRad(params.thetaY);
  const rz = toRad(params.thetaZ);

  _xVec.set(Math.cos(rx) * sx, Math.sin(rx) * sx, 0);
  _yVec.set(Math.cos(ry) * sy, Math.sin(ry) * sy, 0);
  _zVec.set(Math.cos(rz) * sz, Math.sin(rz) * sz, 0);

  _mat.set(
    _xVec.x, _yVec.x, _zVec.x, panOffset.x,
    _xVec.y, _yVec.y, _zVec.y, panOffset.y,
    0, 0, 1, panOffset.z,
    0, 0, 0, 1,
  );

  // 캐시 업데이트
  if (!_cachedMatrix) {
    _cachedMatrix = _mat.clone();
  } else {
    _cachedMatrix.copy(_mat);
  }
  
  if (!_cachedParams) {
    _cachedParams = { ...params };
  } else {
    Object.assign(_cachedParams, params);
  }
  
  _cachedOffset.copy(panOffset);
  _isDirty = false;

  return _mat;
}

/**
 * Group에 Oblique 투영 적용
 */
export function applyObliqueProjection(
  group: THREE.Group,
  matrix: THREE.Matrix4,
): void {
  group.matrixAutoUpdate = false;
  group.matrix.copy(matrix);
  group.matrixWorldNeedsUpdate = true;
}
